/**
 * Cài đặt → Webhook & API: gửi sự kiện của tổ chức (khách mới, bắt SĐT, gắn nhãn, tin nhắn…)
 * sang hệ thống khác. Mỗi đích có khoá ký riêng (hiện một lần), nhật ký gửi và gửi lại.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Copy, KeyRound, Loader2, Plus, RotateCcw, Send, Trash2 } from 'lucide-react'
import { api, apiError } from '@/lib/api-client'
import { useModuleEnabled } from '@/hooks/use-integrations'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

interface SuKien { key: string; ten: string; moTa: string }
interface Dich { id: string; name: string; url: string; events: string[]; isActive: boolean; lastDeliveryAt: string | null; lastStatus: string | null }
interface Luot { id: string; eventId: string; event: string; status: string; attempts: number; responseCode: number | null; error: string | null; createdAt: string; nextAttemptAt: string }

const KHOA = ['settings', 'webhooks']
const MAU_TRANG_THAI: Record<string, 'success' | 'destructive' | 'warning' | 'secondary'> = { success: 'success', dead: 'destructive', failed: 'warning', pending: 'secondary', sending: 'secondary' }
const TEN_TRANG_THAI: Record<string, string> = { success: 'Thành công', dead: 'Bỏ (hết lượt thử)', failed: 'Lỗi — sẽ thử lại', pending: 'Chờ gửi', sending: 'Đang gửi' }

const MA_KIEM_CHU_KY = `// Node.js — kiểm chữ ký trước khi tin dữ liệu
const crypto = require('crypto')
function hopLe(rawBody, header, secret) {
  const mong = 'sha256=' + crypto.createHmac('sha256', secret).update(rawBody).digest('hex')
  return header && crypto.timingSafeEqual(Buffer.from(mong), Buffer.from(header))
}
// header = req.headers['x-webhook-signature'];  bỏ trùng theo req.headers['x-webhook-id']`

function SaoChep({ v }: { v: string }) {
  return <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => { void navigator.clipboard.writeText(v); toast.success('Đã sao chép') }}><Copy className="h-3 w-3" /></Button>
}

function FormDich({ suKien, dau, onXong }: { suKien: SuKien[]; dau?: Dich; onXong: (secret?: string) => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState(dau?.name ?? '')
  const [url, setUrl] = useState(dau?.url ?? '')
  const [events, setEvents] = useState<string[]>(dau?.events ?? ['contact.phone_captured'])
  const luu = useMutation({
    mutationFn: async () => (dau
      ? await api.put<{ endpoint: Dich }>(`/webhooks/${dau.id}`, { name, url, events })
      : await api.post<{ endpoint: Dich; secret: string }>('/webhooks', { name, url, events })).data as { endpoint: Dich; secret?: string },
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: KHOA }); onXong(d.secret) },
    onError: (e) => toast.error(apiError(e)),
  })
  const bat = (k: string, v: boolean) => setEvents((xs) => (v ? [...new Set([...xs, k])] : xs.filter((x) => x !== k)))
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label htmlFor="wh-ten" className="text-xs">Tên</Label><Input id="wh-ten" value={name} onChange={(e) => setName(e.target.value)} placeholder="vd CRM đối tác" className="h-9" /></div>
        <div><Label htmlFor="wh-url" className="text-xs">URL nhận (https)</Label><Input id="wh-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://doitac.vn/webhook/chatmql" className="h-9" /></div>
      </div>
      <div>
        <p className="mb-1 text-xs font-medium">Sự kiện gửi đi</p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {suKien.map((s) => (
            <label key={s.key} htmlFor={`wh-ev-${s.key}`} className="flex items-start gap-2 rounded-md border p-2 text-sm">
              <Checkbox id={`wh-ev-${s.key}`} checked={events.includes(s.key)} onCheckedChange={(v) => bat(s.key, !!v)} className="mt-0.5" />
              <span><span className="font-medium">{s.ten}</span> <code className="text-[10.5px] text-muted-foreground">{s.key}</code><br /><span className="text-xs text-muted-foreground">{s.moTa}</span></span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-end"><Button disabled={luu.isPending || !name || !url || !events.length} onClick={() => luu.mutate()}>{luu.isPending && <Loader2 className="animate-spin" />} {dau ? 'Lưu' : 'Tạo webhook'}</Button></div>
    </div>
  )
}

function NhatKy({ id }: { id: string }) {
  const qc = useQueryClient()
  const { data } = useQuery<{ deliveries: Luot[] }>({ queryKey: [...KHOA, id, 'deliveries'], queryFn: async () => (await api.get(`/webhooks/${id}/deliveries`)).data, refetchInterval: 15_000 })
  const guiLai = useMutation({
    mutationFn: async (lid: string) => (await api.post<{ ok: boolean; error?: string }>(`/webhooks/deliveries/${lid}/retry`)).data,
    onSuccess: (d) => { (d.ok ? toast.success : toast.error)(d.ok ? 'Đã gửi lại thành công' : `Vẫn lỗi: ${d.error ?? ''}`); void qc.invalidateQueries({ queryKey: [...KHOA, id, 'deliveries'] }) },
    onError: (e) => toast.error(apiError(e)),
  })
  if (!data) return <Loader2 className="mx-auto animate-spin text-muted-foreground" />
  if (!data.deliveries.length) return <p className="py-4 text-center text-sm text-muted-foreground">Chưa có lượt gửi nào.</p>
  return (
    <div className="max-h-[360px] divide-y overflow-y-auto rounded-md border text-xs">
      {data.deliveries.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-2 p-2">
          <code className="font-medium">{d.event}</code>
          <Badge variant={MAU_TRANG_THAI[d.status] ?? 'secondary'}>{TEN_TRANG_THAI[d.status] ?? d.status}</Badge>
          <span className="text-muted-foreground">{new Date(d.createdAt).toLocaleString('vi-VN')}</span>
          <span className="text-muted-foreground">· {d.attempts} lần{d.responseCode ? ` · HTTP ${d.responseCode}` : ''}</span>
          {d.error && <span className="text-destructive">{d.error}</span>}
          {(d.status === 'dead' || d.status === 'failed') && <Button size="sm" variant="ghost" className="ml-auto h-6" disabled={guiLai.isPending} onClick={() => guiLai.mutate(d.id)}><RotateCcw className="h-3 w-3" /> Gửi lại</Button>}
        </div>
      ))}
    </div>
  )
}

export function WebhookTab() {
  const qc = useQueryClient()
  const bat = useModuleEnabled('webhooks')
  const { data: ev } = useQuery<{ events: SuKien[] }>({ queryKey: [...KHOA, 'events'], queryFn: async () => (await api.get('/webhooks/events')).data, enabled: bat })
  const { data } = useQuery<{ endpoints: Dich[] }>({ queryKey: KHOA, queryFn: async () => (await api.get('/webhooks')).data, enabled: bat })
  const [taoMoi, setTaoMoi] = useState(false)
  const [sua, setSua] = useState<Dich | null>(null)
  const [xem, setXem] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)

  const thu = useMutation({
    mutationFn: async (id: string) => (await api.post<{ ok: boolean; responseCode?: number; error?: string }>(`/webhooks/${id}/test`)).data,
    onSuccess: (d) => { (d.ok ? toast.success : toast.error)(d.ok ? `Gửi thử thành công (HTTP ${d.responseCode})` : `Gửi thử lỗi: ${d.error ?? `HTTP ${d.responseCode}`}`); void qc.invalidateQueries({ queryKey: KHOA }) },
    onError: (e) => toast.error(apiError(e)),
  })
  const doiKhoa = useMutation({ mutationFn: async (id: string) => (await api.post<{ secret: string }>(`/webhooks/${id}/rotate-secret`)).data, onSuccess: (d) => setSecret(d.secret), onError: (e) => toast.error(apiError(e)) })
  const batTat = useMutation({ mutationFn: async (d: Dich) => (await api.put(`/webhooks/${d.id}`, { isActive: !d.isActive })).data, onSuccess: () => qc.invalidateQueries({ queryKey: KHOA }), onError: (e) => toast.error(apiError(e)) })
  const xoa = useMutation({ mutationFn: async (id: string) => (await api.delete(`/webhooks/${id}`)).data, onSuccess: () => { toast.success('Đã xoá webhook'); void qc.invalidateQueries({ queryKey: KHOA }) }, onError: (e) => toast.error(apiError(e)) })

  if (!bat) return <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">Module <b>Webhook & API</b> chưa được bật cho tổ chức — liên hệ quản trị nền tảng (có trong gói Chuyên nghiệp trở lên).</div>
  if (!data || !ev) return <Loader2 className="mx-auto animate-spin text-muted-foreground" />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-muted-foreground">Hệ thống gửi <b>POST JSON</b> tới URL của bạn mỗi khi có sự kiện, ký bằng HMAC-SHA256; lỗi thì tự thử lại trong 24 giờ. Gọi ngược API bằng khoá API ở mục <b>Mật khẩu & Bảo mật</b> (header <code>x-api-key</code>).</p>
        <Button onClick={() => setTaoMoi(true)}><Plus /> Thêm webhook</Button>
      </div>

      {data.endpoints.length === 0 && <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">Chưa có webhook nào.</div>}
      {data.endpoints.map((d) => (
        <div key={d.id} className="space-y-2 rounded-lg border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{d.name}</span>
            <code className="max-w-[360px] truncate text-xs text-muted-foreground">{d.url}</code>
            {!d.isActive && <Badge variant="secondary">Tạm dừng</Badge>}
            {d.lastStatus && <Badge variant={d.lastStatus.startsWith('ok') ? 'success' : 'warning'}>Lần cuối: {d.lastStatus}</Badge>}
            <div className="ml-auto flex items-center gap-1">
              <Switch checked={d.isActive} onCheckedChange={() => batTat.mutate(d)} aria-label="Bật / tắt" />
              <Button size="sm" variant="outline" disabled={thu.isPending} onClick={() => thu.mutate(d.id)}><Send /> Gửi thử</Button>
              <Button size="sm" variant="ghost" onClick={() => setXem(xem === d.id ? null : d.id)}>Nhật ký</Button>
              <Button size="sm" variant="ghost" onClick={() => setSua(d)}>Sửa</Button>
              <Button size="sm" variant="ghost" title="Đổi khoá ký" onClick={() => { if (confirm('Đổi khoá ký? Bên nhận phải cập nhật khoá mới.')) doiKhoa.mutate(d.id) }}><KeyRound /></Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { if (confirm(`Xoá webhook "${d.name}"?`)) xoa.mutate(d.id) }}><Trash2 /></Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1">{d.events.map((e) => <Badge key={e} variant="secondary" className="font-mono text-[10.5px]">{e}</Badge>)}</div>
          {xem === d.id && <NhatKy id={d.id} />}
        </div>
      ))}

      <details className="rounded-lg border p-3 text-sm">
        <summary className="cursor-pointer font-medium">Định dạng dữ liệu & kiểm chữ ký</summary>
        <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-[11px]">{`POST <URL của bạn>
X-Webhook-Event: contact.phone_captured
X-Webhook-Id: evt_…            (bỏ trùng theo mã này)
X-Webhook-Signature: sha256=…
{ "id": "evt_…", "event": "contact.phone_captured", "createdAt": "…", "orgId": "…",
  "data": { "phone": "0912345678", "action": "set_primary",
            "contact": { "id": "…", "name": "…", "phone": "…" },
            "conversation": { "id": "…", "channel": "…" }, "message": { "id": "…", "text": "…" } } }`}</pre>
        <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-[11px]">{MA_KIEM_CHU_KY}</pre>
        <p className="mt-1 text-xs text-muted-foreground">Trả HTTP 2xx trong 10 giây là thành công; lỗi thì thử lại sau 1 phút, 5 phút, 30 phút, 2 giờ, 6 giờ, 12 giờ, 24 giờ.</p>
      </details>

      <Dialog open={taoMoi || !!sua} onOpenChange={(o) => { if (!o) { setTaoMoi(false); setSua(null) } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{sua ? 'Sửa webhook' : 'Thêm webhook'}</DialogTitle><DialogDescription>URL phải là https và truy cập được từ Internet.</DialogDescription></DialogHeader>
          <FormDich key={sua?.id ?? 'moi'} suKien={ev.events} dau={sua ?? undefined} onXong={(s) => { setTaoMoi(false); setSua(null); if (s) setSecret(s); else toast.success('Đã lưu webhook') }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!secret} onOpenChange={(o) => { if (!o) setSecret(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Khoá ký webhook</DialogTitle><DialogDescription>Chỉ hiện MỘT LẦN — lưu vào hệ thống nhận để kiểm chữ ký.</DialogDescription></DialogHeader>
          {secret && <div className="flex items-center gap-1 rounded-md bg-muted p-2"><code className="flex-1 break-all text-xs">{secret}</code><SaoChep v={secret} /></div>}
          <div className="flex justify-end"><Button onClick={() => setSecret(null)}>Đã lưu khoá</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
